import type { DirectoryEntry } from '../types/api';
import { getLocalAdminSettings } from './adminSync';
import { evaluateSearchMatch } from './searchUtils';

/**
 * Strips redundant parent sheet prefixes (e.g. "Package Delivery - ", "Front Desk ")
 */
export function getCleanTabName(tabName: string | undefined, fullName: string): string {
  let raw = tabName && tabName.trim() ? tabName.trim() : fullName.trim();

  if (raw.includes(' - ')) {
    const parts = raw.split(' - ');
    raw = parts[parts.length - 1].trim();
  }

  const prefixPatterns = [
    /^front\s+desk\s+/i,
    /^package\s+delivery\s+/i,
    /^billing\s+errors\s+/i,
    /^incoming\s+fax\s+/i,
    /^voicemail\s+/i,
    /^repeat\s+calls\s+/i,
    /^ssd\s+staff\s+/i,
  ];

  prefixPatterns.forEach(pattern => {
    raw = raw.replace(pattern, '').trim();
  });

  return raw || fullName;
}

/**
 * Cleans sub-tab display name by removing redundant parent titles or prefixes.
 */
export function getCleanSubTabName(rawTabName: string | undefined, parentTitle?: string): string {
  let name = (rawTabName || '').trim();
  if (!name) return '';

  if (parentTitle) {
    const cleanParent = parentTitle.replace(/\s*-\s*(Dashboard|Sheet|PHI|Active|S\d+-\d+|D\d+-\d+|P\d+-\d+|All SSD).*$/i, '').trim();
    const cleanParentNoMaster = cleanParent.replace(/^Master\s+/i, '').trim();
    for (const prefixCandidate of [cleanParent, cleanParentNoMaster]) {
      if (prefixCandidate && name.toLowerCase().startsWith(prefixCandidate.toLowerCase())) {
        const rest = name.slice(prefixCandidate.length).replace(/^[\s\-–—:]+/, '').trim();
        if (rest) return rest;
      }
    }
  }

  if (name.includes(' - ')) {
    const firstDash = name.indexOf(' - ');
    const prefix = name.slice(0, firstDash).trim();
    const suffix = name.slice(firstDash + 3).trim();
    if (parentTitle && parentTitle.toLowerCase().includes(prefix.toLowerCase()) && suffix) {
      return suffix;
    }
  }

  return name;
}

/**
 * Returns formatted display name for entry. Now driven by Name in Index column (entry.name).
 */
export function getFormattedEntryTitle(entry: DirectoryEntry): string {
  return entry.customDisplayName || entry.name || entry.tabName || 'Untitled Sheet';
}

/**
 * Resolves unique parent spreadsheet key from ssId, Google Sheets URL, or description.
 */
export function getSpreadsheetKey(entry: DirectoryEntry): string {
  if (entry.ssId && entry.ssId.trim()) return entry.ssId.trim();
  if (entry.url) {
    const match = entry.url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (match && match[1]) return match[1];
  }
  if (entry.description && entry.description.trim()) return entry.description.trim();
  return entry.id || entry.name || '';
}

/**
 * Selects canonical/main tab entry from a list of tabs belonging to the same spreadsheet.
 */
export function findMainTabEntry(list: DirectoryEntry[]): DirectoryEntry {
  if (list.length === 0) throw new Error('Empty list in findMainTabEntry');
  if (list.length === 1) return list[0];

  // 1. Tab with gid=0 (Google Sheets primary/default sheet)
  const gid0 = list.find((e) => {
    const url = e.url || '';
    return /[#&?]gid=0(?:&|$)/.test(url);
  });
  if (gid0) return gid0;

  // 2. Tab containing (Main) or starting with "Master "
  const mainOrMaster = list.find((e) => {
    const name = (e.name || '').toLowerCase();
    const tabName = (e.tabName || '').toLowerCase();
    return (
      name.includes('(main)') ||
      tabName.includes('(main)') ||
      name.startsWith('master ') ||
      tabName.startsWith('master ')
    );
  });
  if (mainOrMaster) return mainOrMaster;

  // 3. Tab matching description
  const descMatch = list.find((e) => {
    const desc = (e.description || '').toLowerCase().trim();
    if (!desc) return false;
    const name = (e.name || '').toLowerCase().trim();
    return desc.startsWith(name) || name.includes(desc);
  });
  if (descMatch) return descMatch;

  // 4. Fallback to first entry
  return list[0];
}

/**
 * Groups sheet entries by parent spreadsheet for search results in Column 3 / All Sheets.
 * Prevents multiple tabs of the same spreadsheet from flooding search results.
 * Attaches other worksheet tabs to entry.activeTabs so they remain directly accessible as pill links.
 */
export function groupEntriesBySpreadsheetForSearch(
  entries: DirectoryEntry[],
  searchQuery: string,
  pinnedSet: Set<string>
): Array<{ entry: DirectoryEntry; score: number }> {
  const groups = new Map<string, DirectoryEntry[]>();
  entries.forEach((entry) => {
    const key = getSpreadsheetKey(entry);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(entry);
  });

  const scoredList: Array<{ entry: DirectoryEntry; score: number }> = [];

  groups.forEach((groupEntries) => {
    const mainTab = findMainTabEntry(groupEntries);
    const otherTabs = groupEntries.filter((e) => e !== mainTab);
    const mainTitle = getFormattedEntryTitle(mainTab);

    const mainMatch = evaluateSearchMatch(searchQuery, {
      primary: mainTitle,
      secondary: [
        mainTab.name,
        mainTab.description,
        ...(mainTab.customDisplayName ? [mainTab.customDisplayName] : []),
        ...otherTabs.map((t) => t.name),
      ],
      tertiary: [
        mainTab.team,
        ...(Array.isArray(mainTab.categories) ? mainTab.categories : []),
      ],
    });

    let bestScore = mainMatch.matched ? mainMatch.score : 0;
    let matchedAny = mainMatch.matched;

    const matchingSubTabs: Array<{ name: string; url: string; score: number }> = [];
    const otherSubTabs: Array<{ name: string; url: string; score: number }> = [];

    otherTabs.forEach((tab) => {
      const cleanName = getCleanSubTabName(tab.tabName || tab.name, mainTitle);
      const tabMatch = evaluateSearchMatch(searchQuery, {
        primary: cleanName,
        secondary: [
          tab.name,
          tab.tabName || '',
          tab.description,
          ...(tab.customDisplayName ? [tab.customDisplayName] : []),
        ],
        tertiary: [
          tab.team,
          ...(Array.isArray(tab.categories) ? tab.categories : []),
        ],
      });

      if (tabMatch.matched) {
        matchedAny = true;
        if (tabMatch.score > bestScore) bestScore = tabMatch.score;
        matchingSubTabs.push({ name: cleanName, url: tab.url, score: tabMatch.score });
      } else {
        otherSubTabs.push({ name: cleanName, url: tab.url, score: 0 });
      }
    });

    if (matchedAny) {
      // Prioritize matching sub-tabs first, sorted by score descending
      matchingSubTabs.sort((a, b) => b.score - a.score);

      const seenTabKeys = new Set<string>();
      const allSubTabs = [...matchingSubTabs, ...otherSubTabs].filter((t) => {
        if (!t.name || t.url === mainTab.url || t.name.toLowerCase() === mainTitle.toLowerCase()) {
          return false;
        }
        const key = `${t.name.toLowerCase()}::${t.url}`;
        if (seenTabKeys.has(key)) return false;
        seenTabKeys.add(key);
        return true;
      });

      const isAnyPinned = groupEntries.some((e) => pinnedSet.has(e.id));
      const pinnedId = isAnyPinned
        ? (groupEntries.find((e) => pinnedSet.has(e.id))?.id || mainTab.id)
        : mainTab.id;

      const resultEntry: DirectoryEntry = {
        ...mainTab,
        id: pinnedId,
        activeTabs: allSubTabs.map((t) => ({ name: t.name, url: t.url })),
      };

      scoredList.push({ entry: resultEntry, score: bestScore });
    }
  });

  return scoredList;
}

/**
 * Ensures ALL active, visible worksheet tabs from SCANNED_SPREADSHEET_TABS registry and universal fallbacks are present.
 * Persists and restores custom tab category selections (Main Sheets vs All Sheets) from global admin settings.
 */
export function ensureActiveSheetTabs(rawEntries: DirectoryEntry[]): DirectoryEntry[] {
  const adminSettings = getLocalAdminSettings();
  const savedOverrides = adminSettings.resourcePermissions || {};
  const customSheetTitles = adminSettings.customSheetTitles || {};

  // 0. Deduplicate raw entries first by URL or (docKey + cleanTabName)
  const uniqueRawMap = new Map<string, DirectoryEntry>();
  rawEntries.forEach(entry => {
    const docKey = entry.description || entry.ssId || entry.name;
    const cleanTab = getCleanTabName(entry.tabName, entry.name).toLowerCase();
    const sigKey = entry.url && entry.url.includes('#gid=')
      ? entry.url.toLowerCase().trim()
      : `${docKey.toLowerCase().trim()}::${cleanTab}`;

    if (!uniqueRawMap.has(sigKey)) {
      uniqueRawMap.set(sigKey, entry);
    }
  });
  const deduplicatedRawEntries = Array.from(uniqueRawMap.values());

  const mergedEntries = deduplicatedRawEntries;

  // Final pass: apply savedOverrides and customSheetTitles to ALL merged entries (including raw entries)

  const mappedEntries = mergedEntries.map(entry => {
    const docKey = entry.description || entry.ssId;
    const docOverride = docKey ? (savedOverrides[docKey] || (deduplicatedRawEntries.find(e => (e.description || e.ssId || e.name) === docKey) ? savedOverrides[deduplicatedRawEntries.find(e => (e.description || e.ssId || e.name) === docKey)!.id] : null)) : null;
    const customTitle = customSheetTitles[docKey || ''] || customSheetTitles[entry.description] || customSheetTitles[entry.name];
    const override = savedOverrides[entry.id] || savedOverrides[entry.name] || docOverride;

    let updated = entry;
    if (customTitle || (override && (override as any).customDisplayName)) {
      updated = {
        ...updated,
        customDisplayName: customTitle || (override ? (override as any).customDisplayName : undefined),
      };
    }

    if (override) {
      let overrideTeam = (override.team !== undefined ? override.team : updated.team) || '';
      const tLower = overrideTeam.toLowerCase().trim();
      if (tLower === 'unassigned' || tLower === 'no team' || tLower === 'management') {
        overrideTeam = '';
      }

      updated = {
        ...updated,
        team: overrideTeam,
        categories: override.categories !== undefined ? override.categories : updated.categories,
        accessEmails: override.accessEmails !== undefined ? override.accessEmails : updated.accessEmails,
      };
    }

    return updated;
  });

  // Final Strict Deduplication Pass on Mapped Entries
  const finalUniqueMap = new Map<string, DirectoryEntry>();
  mappedEntries.forEach(entry => {
    const docKey = entry.description || entry.ssId || entry.name;
    const cleanTab = getCleanTabName(entry.tabName, entry.name).toLowerCase();
    const sigKey = entry.url && entry.url.includes('#gid=')
      ? entry.url.toLowerCase().trim()
      : `${docKey.toLowerCase().trim()}::${cleanTab}`;

    if (!finalUniqueMap.has(sigKey)) {
      finalUniqueMap.set(sigKey, entry);
    }
  });

  return Array.from(finalUniqueMap.values());
}
